import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';

const DEFAULT_MATCH_TYPES = ['BROAD', 'PHRASE', 'EXACT'] as const;

const label = (mt: string) => mt.charAt(0) + mt.slice(1).toLowerCase();

interface MatchTypeSelectProps {
  matchType: string | null | undefined;
  onChange: (matchType: string) => void;
  /** Pill color classes for a match type — each table keeps its own palette. */
  pillClasses: (matchType: string) => string;
  /** Option order (and the fallback when matchType is empty is the first entry). */
  matchTypes?: readonly string[];
  /** Extra trigger classes — callers control alignment (e.g. `mx-auto` for a centered column). */
  className?: string;
  disabled?: boolean;
}

/**
 * Inline editable match-type pill (#5555): a rounded-full `Select` trigger styled as a colored pill,
 * mirroring the competitor ProximitySelect. Shared by the keyword and negative-keyword tables.
 *
 * Note: changing a keyword's match type is destructive (Google Ads retire-and-recreates it). The
 * keyword table gates this behind a confirm; negatives are draft edits with no history, so they
 * commit directly. The confirm/commit policy lives in the parent, not here.
 */
export function MatchTypeSelect({
  matchType,
  onChange,
  pillClasses,
  matchTypes = DEFAULT_MATCH_TYPES,
  className = '',
  disabled,
}: MatchTypeSelectProps) {
  const current = (matchType || matchTypes[0]).toUpperCase();
  return (
    <Select value={current} onValueChange={(mt) => { if (mt !== current) onChange(mt); }} disabled={disabled}>
      <SelectTrigger
        onClick={(e) => e.stopPropagation()}
        className={`h-auto w-fit gap-1 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium focus:ring-1 focus:ring-[#290BD9] [&>svg]:static [&>svg]:ml-0.5 [&>svg]:size-3 [&>svg]:opacity-60 ${pillClasses(current)} ${className}`}
      >
        {label(current)}
      </SelectTrigger>
      <SelectContent>
        {matchTypes.map((mt) => (
          <SelectItem key={mt} value={mt} className="text-xs">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${pillClasses(mt)}`}>
              {label(mt)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
